import { useCallback, useEffect, useRef } from 'react';
import type { PlayerProfile } from '@goodtime/engine';
import { authHeaders, BASE } from '../api/client';

/** B' (2026-05-31) — when the auto-reload fires server-side (the
 *  player busts during a wheel match), the post-refill wallet always
 *  lands at exactly this many coins. Matches the server constant
 *  {@code ZeroBalanceRefillPostProcessor.REFILL_AMOUNT} + the client
 *  constant {@code ZERO_BALANCE_TOP_UP} in {@code PlayerProfile}.
 *  Used for the {@code from} field on the synthesized
 *  {@code zero-balance-refill} event so {@link RefillBurst}'s counter
 *  roll starts from the right baseline. */
const ZERO_BALANCE_REFILL_AMOUNT = 1000;

/**
 * Server → client wallet reconciliation.
 *
 * <p>The mobile {@link PlayerProfile} maintains a local cache of coins
 * + gems (LocalStorage-backed) for immediate UX. The server is the
 * source of truth for wallet mutations that happen inside the room
 * engine — wheel-match gem payouts (T1 random drops from reels /
 * wheel / podium, 2026-05-29), slot-spin coin wins, etc. — but none
 * of those mutations propagate back to the client until something
 * explicitly fetches the wallet.
 *
 * <p>This hook is the glue. Call {@code sync()} after any event that
 * may have credited gems or coins server-side (most importantly: the
 * PAYOUT transition of a wheel match). It GETs the wallet, diffs
 * against the local profile, and applies the credit via
 * {@code awardGems(delta)} so the standard {@code gems-changed} event
 * fires — picked up by the wallet pill chrome and by the
 * {@link GemDropToast} for the celebration beat.
 *
 * <p>Debits (server gems < local gems) shouldn't happen via this
 * path under normal play — gems can only be SPENT through the
 * {@code spend-gems-for-slot} endpoint which already updates the
 * local profile via {@link useSlotUnlockSync}. If a debit IS detected
 * (e.g. a server-side admin tool clawed back gems), we silently
 * reconcile by calling {@code spendGems(delta)} — no toast.
 *
 * <p>Null/missing playerId → no-op. Network errors → warn AND
 * (U12 M4, 2026-05-31) emit a {@code sync-error} event on the profile
 * so the host's {@code SyncErrorBanner} can surface "Couldn't sync
 * your gems". Local profile stays authoritative until the next
 * successful sync; we don't roll back local credits on a sync failure
 * (the typical case is a momentary network drop after a match
 * completes, and rolling back the celebration would be worse UX than
 * the silent divergence).
 */
export function useWalletSync(
  playerId: string | null,
  profile: PlayerProfile,
): { sync: () => Promise<void> } {
  // Hold the latest profile in a ref so the sync callback doesn't
  // need to be recreated on every render — keeping it stable means
  // callers can wire `sync` into useEffect deps without re-firing.
  const profileRef = useRef(profile);
  profileRef.current = profile;

  // U12 #1 — cold-sync toast suppression. The very FIRST sync after
  // mount reconciles the local gem cache against the server's
  // authoritative value. If the player closed the app mid-match (or
  // the server credited gems via some other path), the diff at boot
  // could be large — and {@link GemDropToast} would fire a misleading
  // "+N 💎" celebration. The player didn't earn it just now. Track a
  // "first sync done" flag here; on the first call we use
  // {@code profile.reconcileGems} (silent), on subsequent calls we
  // use {@code awardGems} (fires the toast). New player flow → first
  // sync sees server=100 = local=100, no diff, no-op either way.
  const firstSyncDoneRef = useRef(false);

  // B' (2026-05-31) — track the last refill epoch we've seen so we
  // only fire {@link RefillBurst} once per server-side refill. First
  // sync seeds the ref to the server's value (silent — we don't want
  // a celebration on every app boot, only on advances). Subsequent
  // syncs fire if the value advanced. {@code null} means "never been
  // refilled" — we treat null and equal-to-last as no-op.
  const lastSeenRefillAtMsRef = useRef<number | null>(null);

  const sync = useCallback(async (): Promise<void> => {
    if (!playerId) return;
    try {
      // Use the shared API base (includes /v1) so this resolves to the API
      // origin in prod — a relative /v1 hits the web host (play.goodtime.casino),
      // which has no backend, and the sync silently fails.
      const res = await fetch(
        `${BASE}/players/${encodeURIComponent(playerId)}/wallet`,
        { headers: await authHeaders() },
      );
      if (!res.ok) {
        const reason = `HTTP ${res.status}`;
        console.warn(
          '[useWalletSync] non-OK response',
          res.status,
          await res.text().catch(() => ''),
        );
        profileRef.current.events.emit({
          kind: 'sync-error',
          operation: 'wallet-sync',
          message: reason,
        });
        return;
      }
      const wallet = (await res.json()) as {
        coins: number;
        gems: number;
        lastRefillAtMs?: number | null;
        xp?: number;
      };
      const p = profileRef.current;
      const localGems = p.gems;
      const serverGems = wallet.gems;
      const isFirstSync = !firstSyncDoneRef.current;
      if (isFirstSync) {
        // Cold reconciliation — silent, no toast. Handles both
        // directions in one shot.
        firstSyncDoneRef.current = true;
        p.reconcileGems(serverGems);
      } else if (serverGems > localGems) {
        // Live credit — server earned gems we don't know about yet
        // (match payout, server-side admin grant). Award the diff
        // through {@code awardGems} so {@code gems-changed} fires
        // and the {@link GemDropToast} celebrates.
        p.awardGems(serverGems - localGems);
      } else if (serverGems < localGems) {
        // Live debit — uncommon under normal play (e.g. a server-side
        // clawback while we were online). Suppress the toast by not
        // awarding; the spend path doesn't trip a positive credit.
        p.spendGems(localGems - serverGems);
      }
      // B' (2026-05-31) — coin reconciliation. The wheel-room engine
      // applies bet debits + payout credits through {@code WalletDelta}
      // server-side; the client's local profile balance only mirrors
      // slot-scene activity (which is server-driven inside REEL_RACE
      // anyway via {@code SpinSlotResponse.wallet}). At wheel-match
      // PAYOUT the local profile balance lags the server by exactly
      // the per-match net change. Snap to the server's value here.
      //
      // The "double-count" concern from the original implementation
      // doesn't apply: the slot scene's local debit + credit either
      // (a) was reflected in the same /sessions/{id}/spin response
      // that already updated the model + profile, OR (b) is part of
      // the wheel-match path the server is fully authoritative for.
      // Either way, server.coins is the truth.
      if (wallet.coins !== p.balance) {
        if (isFirstSync) {
          // Cold reconcile — silent. The boot correction (local
          // placeholder, e.g. 1000, → real server balance, e.g. 500) is
          // NOT a wager: routing it through {@code setBalance} would
          // award XP for the downward delta, minting phantom level-ups
          // and their level-up gems (the boot "lost ~600 coins + jumped
          // to Lvl 6 / +35 gems on first spin" bug). {@code
          // reconcileBalance} just snaps the value + fires
          // {@code balance-changed} for the HUD.
          p.reconcileBalance(wallet.coins);
        } else {
          // Live sync (e.g. wheel-match PAYOUT) — the per-match net
          // change is a genuine wager; keep the XP-on-debit behaviour.
          p.setBalance(wallet.coins);
        }
      }
      // The server counts every paid bet (slot spins and wheel-room bets)
      // as XP and credits the level-up gems itself, so its XP is the
      // player's level. Adopt it after the coins: a live sync's setBalance
      // above may have counted the net loss locally first. Silent on the
      // first sync, like the gems. A server without xp (older than
      // 2026-10-06) leaves the level gems to the profile.
      const serverCountsXp = typeof wallet.xp === 'number';
      p.setServerWallet(serverCountsXp);
      if (serverCountsXp) p.reconcileXp(wallet.xp as number, !isFirstSync);
      // Did the server-side auto-reload fire since we last looked?
      // {@code lastRefillAtMs} comes from the wallet endpoint
      // computing MAX(applied_at) over wallet_deltas with reason =
      // 'zero_balance_refill'. The first sync seeds the ref silently
      // so we don't celebrate every app boot; subsequent syncs fire
      // {@code zero-balance-refill} on advance. {@link RefillBurst}
      // mounted in App.tsx listens for this and plays the brand-
      // promise overlay.
      const serverRefillAtMs = wallet.lastRefillAtMs ?? null;
      if (isFirstSync) {
        lastSeenRefillAtMsRef.current = serverRefillAtMs;
      } else if (
        serverRefillAtMs !== null &&
        serverRefillAtMs !== lastSeenRefillAtMsRef.current
      ) {
        lastSeenRefillAtMsRef.current = serverRefillAtMs;
        p.events.emit({
          kind: 'zero-balance-refill',
          amount: ZERO_BALANCE_REFILL_AMOUNT,
          from: 0,
          to: wallet.coins,
        });
      }
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      console.warn('[useWalletSync] sync failed', e);
      profileRef.current.events.emit({
        kind: 'sync-error',
        operation: 'wallet-sync',
        message: reason,
      });
    }
  }, [playerId]);

  return { sync };
}

/**
 * Convenience wrapper — fires {@code sync()} whenever {@code trigger}
 * value changes (e.g. the wheel-room phase). The hook debounces
 * against rapid toggles by tracking the last trigger value.
 */
export function useWalletSyncOnTrigger(
  playerId: string | null,
  profile: PlayerProfile,
  trigger: string | number | null,
): void {
  const { sync } = useWalletSync(playerId, profile);
  const lastTriggerRef = useRef<typeof trigger>(null);
  useEffect(() => {
    if (trigger === null) return;
    if (trigger === lastTriggerRef.current) return;
    lastTriggerRef.current = trigger;
    void sync();
  }, [trigger, sync]);
}
